import React, { useState, useEffect } from 'react'
import ListItemText from '@material-ui/core/ListItemText'
import Menu from '@material-ui/core/Menu'
import { color as d3Color } from 'd3-color'
import { GraphHeading, StyledButton, colors, StyledMenuItem } from '../../../theme/Global'
import Choropleth, { stateName, stateOrCountryNameByAbv, GEO_MAP } from '../../../common/components/drawMap'
import { StyledPaper } from '../../../theme/Table'
import { formatDate } from '../../../common/helpers'
import PosterModal from '../posterModal'
import { sorts } from '../../../app/components/dataTable/withDataTable'
import { postersPageStyle, PostersControls } from './styles'
import { getPostersData, queryLocations } from './service'
import { Modal } from '../../../common/components/modal'
import DefaultErrorMessage from '../../../common/components/ErrorBoundary/errorMessage'
import { StyledCountryButton, StyledCountrySpan, StyledDropDownIcon } from '../styles'

const PostersMap = () => {
  const [posters, setPosters] = useState([])
  const [modalOpen, setModalOpen] = useState(false)
  const [isCountryMenuOpen, setIsCountryMenuOpen] = useState(false)
  const [countryMenuAnchor, setCountryMenuAnchor] = useState(null)
  const [isPostersMenuOpen, setIsPostersMenuOpen] = useState(false)
  const [postersMenuAnchor, setPostersMenuAnchor] = useState(null)
  const [countries, setCountries] = useState([])
  const [selectedCountry, setSelectedCountry] = useState(GEO_MAP.US)
  const [selectedPoster, setSelectedPoster] = useState({})
  const [local, setLocal] = useState()
  const [error, setError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const allowedJurisdictions = ['state', 'province', 'territory', 'federal']

  const fetchPosters = async (subscribed) => {
    setPosters([])

    if (subscribed) {
      try {
        const postersData = await getPostersData()
        setPosters(sortPosters(postersData))
      } catch (e) {
        console.error('getPostersDataError', e || 'unknown error')
        setError(e)
      }
    }

    return null
  }

  const fetchLocaleFacets = async (subscribed) => {
    if (subscribed) {
      try {
        const locationsData = await queryLocations()
        const localeFacets = locationsData.data.localeFacets || []
        setAvailableCountries(localeFacets)
      } catch (e) {
        console.error('queryLocationsError', e || 'unknown error')
        setError(e)
      }
    }

    return null
  }

  useEffect(() => {
    let subscribed = true
    setIsLoading(true)

    Promise.all([fetchPosters(subscribed), fetchLocaleFacets(subscribed)])
      .then(() => {
        if (subscribed) {
          setIsLoading(false)
        }
      })

    return () => {
      subscribed = false
    }
  }, [])

  const setAvailableCountries = (localeFacets) => {
    const validCountryCodes = ['US', 'CA']
    const validTerritoryCodes = ['PR', 'VI', 'GU']
    const foundCountriesTerritories = []

    const addCodeIfValid = (code, validCodes) => {
      if (code && !foundCountriesTerritories.includes(code) && validCodes.includes(code)) {
        foundCountriesTerritories.push(code)
      }
    }

    localeFacets.map((facet) => {
      const [countryCode, stateCode] = facet.facet.split('~')

      if (!countryCode || !stateCode) {
        console.error(`Bad data in locationMap from facet~addresses_shipping_country-state, facet: ${JSON.stringify(facet)}`)
      }

      addCodeIfValid(countryCode, validCountryCodes)
      addCodeIfValid(stateCode, validTerritoryCodes)

      setCountries(foundCountriesTerritories.sort())

      if (!foundCountriesTerritories.includes(selectedCountry.code)) {
        setSelectedCountry(GEO_MAP[foundCountriesTerritories[0]])
      }

      return null
    })
  }

  const sortPosters = (postersToSort) => {
    const federalPosters = []

    let sortedPosters = postersToSort.sort((a, b) => {
      return sorts.amount(a.jurisdictionName, b.jurisdictionName)
    })

    sortedPosters = sortedPosters.filter((item) => {
      if (item && item.jurisdictionType && item.jurisdictionType.toLowerCase().includes('federal')) {
        if (item.downloadUrl && item.complianceDate) {
          federalPosters.push(item)
        }
        return false
      }
      return true
    })

    if (federalPosters.length > 0) {
      sortedPosters.unshift(...federalPosters)
    }

    return sortedPosters
  }

  const getCountryCode = (datum) => { // hard-coding for Puerto Rico, Virgin Islands, and Guam
    const prCode = datum.properties.GID_0 === 'PRI' ? 'PR' : null
    const viCode = datum.properties.adm0_a3 === 'VIR' ? 'VI' : null
    const guCode = datum.properties.hasc === 'GU' ? 'GU' : null
    return prCode || viCode || guCode || datum.properties.postal
  }

  const drawTooltip = (dataSet, datum) => {
    if (datum && datum.properties) {
      const provinceCode = getCountryCode(datum)
      const data = dataSet.find((x) => {
        if (x.stateProvince !== provinceCode) {
          return false
        }
        return allowedJurisdictions.find((jurisdiction) => x.jurisdictionType.toLowerCase()
          .includes(jurisdiction))
      })

      if (data !== undefined) {
        return `
            <p style="margin-top: 0px;font-weight: 600;">
                ${stateName(datum)}
            </p>
            <p style="margin-bottom: 0px">
                Compliant as of: ${data.complianceDate ? formatDate(data.complianceDate) : 'N/A'}
            </p>
            Poster Size: ${data.size || 'N/A'}`
      }
    }
    return undefined
  }

  const onShowPoster = (poster) => {
    // Find all local posters for this state
    let localPosters = posters.filter((item) => {
      if (!item.country || item.country.toLowerCase() !== selectedCountry.code.toLowerCase()) {
        return false
      }
      if (!item.stateProvince || item.stateProvince.toLowerCase() !== poster.stateProvince.toLowerCase()) {
        return false
      }

      return true
    })
    // Sort locals alphabetical
    localPosters = localPosters.sort((a, b) => {
      if (a.jurisdictionName === b.jurisdictionName) {
        return 0
      }
      if (a.jurisdictionName > b.jurisdictionName) {
        return 1
      }
      return -1
    })

    setLocal(localPosters)
    setSelectedPoster(poster)
    setModalOpen(true)
  }

  const openCountryMenu = (e) => {
    setIsCountryMenuOpen(true)
    setCountryMenuAnchor(e.currentTarget)
  }

  const closeCountryMenu = () => {
    setIsCountryMenuOpen(false)
    setCountryMenuAnchor(null)
  }

  const openPostersMenu = (e) => {
    setIsPostersMenuOpen(true)
    setPostersMenuAnchor(e.currentTarget)
  }

  const closePostersMenu = () => {
    setIsPostersMenuOpen(false)
    setPostersMenuAnchor(null)
  }

  const onMapClick = (mapElement) => {
    if (mapElement && mapElement.properties) {
      const provinceCode = getCountryCode(mapElement)
      const poster = posters.find((x) => {
        if (x.stateProvince !== provinceCode) {
          return false
        }
        if (!allowedJurisdictions.find((jurisdiction) => x.jurisdictionType.toLowerCase().includes(jurisdiction))) {
          return false
        }
        return true
      })

      if (poster !== undefined) {
        onShowPoster(poster, selectedCountry)
      }
    }
  }

  const fillForState = (statePosters, state) => {
    if (statePosters.findIndex((poster) => poster.stateProvince === state) > -1) {
      return colors.govDocsBlue
    }
    return colors.lightGrey
  }

  const strokeForSate = (poster, state) => {
    return d3Color(fillForState(poster, state)).darker()
  }

  return (error)
    ? <DefaultErrorMessage error={error} data-testid="error-container" />
    : (
      <React.Fragment>
        <PostersControls>
          <GraphHeading>
            {countries.length > 1
              ? <StyledCountryButton data-testid="country-button" style={{ backgroundColor: colors.backgroundGrey, marginLeft: '-30px' }} disabled={isLoading} onClick={openCountryMenu}><StyledDropDownIcon />{selectedCountry.name} Posters</StyledCountryButton>
              : <StyledCountrySpan data-testid="country-button" style={{ backgroundColor: colors.backgroundGrey, marginLeft: '-30px' }}>{selectedCountry.name} Posters</StyledCountrySpan>}
          </GraphHeading>
          <React.Fragment>
            <StyledButton onClick={openPostersMenu} data-testid="posters-button">
              Select a Poster
            </StyledButton>
            <Menu
              data-testid="posters-menu"
              anchorEl={postersMenuAnchor}
              open={isPostersMenuOpen}
              onClose={closePostersMenu}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left'
              }}
              getContentAnchorEl={null}
              PaperProps={{
                style: {
                  maxHeight: 125 * 4.5,
                  width: '41ch'
                }
              }}
            >
              {posters.map((item) => {
                if (!allowedJurisdictions.find((jurisdiction) => item.jurisdictionType && item.jurisdictionType.toLowerCase().includes(jurisdiction))) {
                  return null
                }

                if (item.country !== selectedCountry.code) {
                  return null
                }

                return (
                  <StyledMenuItem
                    data-testid={`posters-menu-${item.sku}`}
                    key={item.sku}
                    onClick={() => { onShowPoster(item, selectedCountry) }}
                  >
                    <ListItemText
                      style={{ minWidth: '140px' }}
                      primary={item.jurisdictionType.toLowerCase().includes('federal') ? item.jurisdictionName : stateOrCountryNameByAbv(item.stateProvince, selectedCountry)}
                      secondary={`Poster Size: ${item.size}`}
                    />
                    <ListItemText
                      primary="Compliant as of:"
                      secondary={item.complianceDate ? formatDate(item.complianceDate) : 'N/A'}
                    />
                  </StyledMenuItem>
                )
              })}
            </Menu>
          </React.Fragment>
          <React.Fragment>
            <Menu
              anchorEl={countryMenuAnchor}
              open={isCountryMenuOpen}
              onClose={closeCountryMenu}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left'
              }}
              getContentAnchorEl={null}
            >
              {countries.map((item) => {
                return (
                  <StyledMenuItem
                    data-testid={`country-menu-${GEO_MAP[item].code}`}
                    key={GEO_MAP[item].name}
                    onClick={() => {
                      setSelectedCountry(GEO_MAP[item])
                      closeCountryMenu()
                    }}
                  >
                    {GEO_MAP[item].name}
                  </StyledMenuItem>
                )
              })}
            </Menu>
          </React.Fragment>
        </PostersControls>
        <StyledPaper style={postersPageStyle}>
          <Choropleth
            mapData={posters}
            getFillForState={fillForState}
            getStrokeForState={strokeForSate}
            getTooltipHtml={drawTooltip}
            onMapClick={onMapClick}
            country={selectedCountry.code}
            parentIsLoading={isLoading}
          />
        </StyledPaper>
        <Modal
          styles={{ maxWidth: '900px' }}
          open={modalOpen}
          title={`Labor Law Postings in ${stateOrCountryNameByAbv(selectedPoster.stateProvince, selectedCountry)}`}
          component={(
            <PosterModal
              complianceDate={selectedPoster.complianceDate}
              downloadUrl={selectedPoster.downloadUrl}
              local={local}
              name={selectedPoster.jurisdictionName}
              size={selectedPoster.size}
            />
          )}
          modalCloseDispatch={() => { setModalOpen(false) }}
        />
      </React.Fragment>
    )
}

export default PostersMap
